/**
 * Bases (waffle cone, paper cup), the eight speckled scoops, toppings, and the
 * serving dish. Art ported from the "Scoops & Smiles" asset kit: a thick warm
 * outline made by layering a dark cluster behind a colored cluster, plus little
 * seeds/chips/chunks per flavor.
 */
import { FLAVORS, UI, INK, SPRINKLE_COLORS, type Flavor } from '../palette.ts';
import { svgDoc, ellipse, group } from '../primitives.ts';

// Scoop "cluster" — six overlapping balls. The dark layer is 8px bigger than
// the colored layer, so 6px of outline peeks out all the way around the blob.
// viewBox 200×175; the blob's visual center is ~(100, 86).
const CLUSTER: Array<[number, number, number, number]> = [
  // [cx, cy, darkR, colorR]
  [100, 95, 62, 54],
  [52, 112, 44, 36],
  [148, 112, 44, 36],
  [72, 60, 42, 34],
  [128, 60, 42, 34],
  [100, 46, 38, 30],
];

// Deterministic speck scatter (stable build-to-build).
const SPECK_SPOTS: Array<[number, number, number]> = [
  [72, 72, 18],
  [108, 58, -12],
  [132, 92, 24],
  [95, 100, -20],
  [60, 104, 10],
  [124, 120, 16],
  [86, 124, -8],
];

function specks(f: Flavor): string {
  if (f.speckStyle === 'none') return '';
  const s = f.speckSize;
  const marks = SPECK_SPOTS.map(([x, y, rot]) => {
    if (f.speckStyle === 'chunk') {
      const w = s * 2.1;
      const h = s * 1.7;
      return `<rect x="${x - w / 2}" y="${y - h / 2}" width="${w}" height="${h}" rx="${h / 2}" transform="rotate(${rot} ${x} ${y})"/>`;
    }
    return `<circle cx="${x}" cy="${y}" r="${s}"/>`;
  }).join('');
  return `<g fill="${f.speck}">${marks}</g>`;
}

export function scoop(f: Flavor): string {
  const dark = CLUSTER.map(([cx, cy, r]) => `<circle cx="${cx}" cy="${cy}" r="${r}"/>`).join('');
  const color = CLUSTER.map(([cx, cy, , r]) => `<circle cx="${cx}" cy="${cy}" r="${r}"/>`).join('');
  const sheen = ellipse(78, 60, 18, 11, { fill: '#ffffff', stroke: 'none', opacity: 0.45 });
  return svgDoc(
    200,
    175,
    `<g fill="${INK}">${dark}</g><g fill="${f.color}">${color}</g>${specks(f)}${sheen}`,
  );
}

// ── Waffle cone ──────────────────────────────────────────────────────────────
// viewBox 160×200. The opening (where the first scoop seats) is centered at
// (80, 46).
export function cone(): string {
  const body =
    `<defs><clipPath id="coneClip"><path d="M30,46 L130,46 L80,188 Z"/></clipPath></defs>` +
    `<ellipse cx="80" cy="46" rx="52" ry="13" fill="${UI.coneLight}" stroke="${INK}" stroke-width="6"/>` +
    `<path d="M30,46 L130,46 L80,188 Z" fill="${UI.cone}" stroke="${INK}" stroke-width="6" stroke-linejoin="round"/>` +
    `<g clip-path="url(#coneClip)" stroke="${UI.coneDark}" stroke-width="3.5" fill="none">` +
    `<path d="M-10,60 L170,60 M-10,90 L170,90 M-10,120 L170,120 M-10,150 L170,150"/>` +
    `<path d="M30,30 L30,210 M55,30 L55,210 M80,30 L80,210 M105,30 L105,210 M130,30 L130,210" transform="skewX(-18)"/>` +
    `</g>` +
    `<ellipse cx="80" cy="46" rx="52" ry="13" fill="none" stroke="${INK}" stroke-width="6"/>`;
  return svgDoc(160, 200, body);
}

// ── Paper cup (pink, with a little flower) ────────────────────────────────────
// viewBox 170×190. Rim opening centered at (85, 62).
export function cup(): string {
  const body =
    `<path d="M40,62 L130,62 L118,176 a8,8 0 0 1 -8,7 L60,183 a8,8 0 0 1 -8,-7 Z" fill="${UI.cupBody}" stroke="${INK}" stroke-width="6" stroke-linejoin="round"/>` +
    `<ellipse cx="85" cy="62" rx="45" ry="12" fill="${UI.cupBand}" stroke="${INK}" stroke-width="6"/>` +
    `<circle cx="85" cy="128" r="24" fill="#ffffff"/>` +
    group(flowerPetals(0, 0, 13, UI.cupBody, UI.flowerYellow), 'translate(85,128)');
  return svgDoc(170, 190, body);
}

/** Five petals + a center dot, drawn around (cx,cy). Shared with the jar lid. */
function flowerPetals(cx: number, cy: number, r: number, petal: string, center: string): string {
  const petals: string[] = [];
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
    petals.push(
      `<ellipse cx="${cx + Math.cos(a) * (r * 0.7)}" cy="${cy + Math.sin(a) * (r * 0.7)}" rx="${r * 0.5}" ry="${r * 0.72}" fill="${petal}" transform="rotate(${(i / 5) * 360} ${cx} ${cy})"/>`,
    );
  }
  return petals.join('') + `<circle cx="${cx}" cy="${cy}" r="${r * 0.46}" fill="${center}"/>`;
}

/** A small 5-petal flower (white petals, yellow center) for UI decoration. */
export function flowerMark(cx: number, cy: number, r: number): string {
  return flowerPetals(cx, cy, r, '#ffffff', UI.flowerYellow);
}

// ── Toppings ──────────────────────────────────────────────────────────────────
// Cherry — viewBox 160×190; berry centered low so it perches on the stack.
export function cherry(): string {
  const body =
    `<path d="M82,128 C86,72 104,52 134,46" fill="none" stroke="${UI.cherryStem}" stroke-width="9" stroke-linecap="round"/>` +
    `<path d="M134,46 q20,-12 30,2 q-12,18 -30,-2 Z" fill="${UI.cherryLeaf}" stroke="${INK}" stroke-width="5" stroke-linejoin="round"/>` +
    `<circle cx="78" cy="138" r="42" fill="${UI.cherry}" stroke="${INK}" stroke-width="6"/>` +
    `<ellipse cx="62" cy="120" rx="11" ry="8" fill="#ffffff" opacity="0.55"/>`;
  return svgDoc(160, 190, body);
}

/** A scattered heap of rainbow sprinkles. viewBox 190×160. */
export function sprinkles(): string {
  const spots: Array<[number, number, number]> = [
    [40, 96, -22],
    [74, 104, 12],
    [110, 98, -14],
    [58, 74, 28],
    [92, 70, -32],
    [124, 80, 18],
    [44, 58, -8],
    [98, 98, 40],
    [70, 86, -44],
    [116, 60, 8],
    [80, 50, 22],
    [36, 80, 30],
  ];
  const sp = spots
    .map(([x, y, rot], i) => {
      const c = SPRINKLE_COLORS[i % SPRINKLE_COLORS.length];
      return `<rect x="${x}" y="${y}" width="26" height="11" rx="5.5" fill="${c}" stroke="${INK}" stroke-width="3.5" transform="rotate(${rot} ${x + 13} ${y + 5.5})"/>`;
    })
    .join('');
  return svgDoc(190, 160, sp);
}

/** A rolled wafer biscuit (pirouette). viewBox 190×160. */
export function wafer(): string {
  const body =
    `<g transform="rotate(-26 95 80)">` +
    `<defs><clipPath id="waferClip"><rect x="30" y="58" width="130" height="44" rx="22"/></clipPath></defs>` +
    `<rect x="30" y="58" width="130" height="44" rx="22" fill="${UI.wafer}" stroke="${INK}" stroke-width="6"/>` +
    `<g clip-path="url(#waferClip)" stroke="${UI.waferDark}" stroke-width="9" stroke-linecap="round">` +
    `<path d="M48,50 L24,112 M78,50 L54,112 M108,50 L84,112 M138,50 L114,112"/>` +
    `</g>` +
    `<ellipse cx="150" cy="80" rx="9" ry="22" fill="${UI.waferEnd}" stroke="${INK}" stroke-width="6"/>` +
    `<ellipse cx="150" cy="80" rx="3.5" ry="11" fill="${UI.waferDark}"/>` +
    `</g>`;
  return svgDoc(190, 160, body);
}

/** A soft swirl of whipped cream. viewBox 160×200. */
export function whip(): string {
  const dark =
    `<g fill="${INK}"><circle cx="80" cy="152" r="50"/><circle cx="70" cy="116" r="40"/><circle cx="92" cy="88" r="32"/><circle cx="78" cy="60" r="22"/><circle cx="84" cy="38" r="13"/></g>`;
  const cream =
    `<g fill="${UI.whip}"><circle cx="80" cy="152" r="42"/><circle cx="70" cy="116" r="32"/><circle cx="92" cy="88" r="24"/><circle cx="78" cy="60" r="14"/><circle cx="84" cy="38" r="6"/></g>`;
  const folds =
    `<g fill="none" stroke="${UI.whipShade}" stroke-width="5.5" stroke-linecap="round">` +
    `<path d="M44,140 q24,22 56,4"/><path d="M42,112 q22,20 50,2"/><path d="M56,84 q16,16 38,0"/></g>`;
  const shine = `<ellipse cx="96" cy="78" rx="8" ry="5" fill="#ffffff"/>`;
  return svgDoc(160, 200, dark + cream + folds + shine);
}

// ── Serving dish (the cone stands on this) ────────────────────────────────────
export function plate(): string {
  const dish = ellipse(120, 44, 112, 32, { fill: UI.plate, stroke: INK });
  const top = ellipse(120, 34, 88, 22, { fill: UI.plateLight, stroke: 'none' });
  const shine = ellipse(82, 30, 24, 8, { fill: '#ffffff', stroke: 'none', opacity: 0.5 });
  return svgDoc(240, 88, dish + top + shine);
}

/** Build every food texture keyed by registry name. */
export function foodTextures(): Record<string, { svg: string; w: number; h: number }> {
  const out: Record<string, { svg: string; w: number; h: number }> = {
    base_cone: { svg: cone(), w: 160, h: 200 },
    base_cup: { svg: cup(), w: 170, h: 190 },
    top_cherry: { svg: cherry(), w: 160, h: 190 },
    top_sprinkles: { svg: sprinkles(), w: 190, h: 160 },
    top_wafer: { svg: wafer(), w: 190, h: 160 },
    top_whip: { svg: whip(), w: 160, h: 200 },
    plate: { svg: plate(), w: 240, h: 88 },
  };
  for (const f of FLAVORS) {
    out[`scoop_${f.id}`] = { svg: scoop(f), w: 200, h: 175 };
  }
  return out;
}
